import {useEffect, useState} from 'react'
import _ from 'lodash'
import {Modal} from 'semantic-ui-react'
import {ErrorMessage} from '@/features/errors'
import {JsonViewer} from '@/components/json-viewer'
import {ExpressionEditor, IExpressionConfig, IExpressionEditorProps} from '../editor'
import './style.css'

export type ExpressionConfigMap = Record<string, IExpressionConfig>

export interface IExpressionMapEditorProps<TConf = ExpressionConfigMap>
  extends Omit<IExpressionEditorProps, 'value' | 'onChange' | 'onRunExpression'> {
  keys?: string[]
  values?: TConf
  errors?: {[key: string]: string[]}
  onChange?: (expression: TConf) => void
  onRunExpression?: (expression: TConf) => void
}

export function ExpressionMapEditor(props: IExpressionMapEditorProps) {
  const {keys, isEditableAsJson, values, errors, onChange, onRunExpression, ...rest} = props

  const [mapValue, setMapValue] = useState<{[key: string]: IExpressionConfig}>(values || {})
  const [currentJson, setCurrentJson] = useState(values ? JSON.stringify(values) : '')

  useEffect(() => {
    if (!onChange) return
    if (_.isEqual(values, mapValue)) return
    onChange(mapValue)
  }, [mapValue, onChange])

  // TODO: useEffect?
  function handleOnRunExpression(key: string, data: IExpressionConfig) {
    const _expression: {[key: string]: IExpressionConfig} = {
      ...mapValue,
      [key]: data,
    }
    if (onRunExpression) onRunExpression(_expression)
  }

  return (
    <div className='expression-editor map'>
      {isEditableAsJson ?
        <div className='actions'>
          <Modal
            size='tiny'
            trigger={<div>json</div>}
            onOpen={() => {
              setCurrentJson(JSON.stringify(mapValue, undefined, 2))
            }}
            onClose={() => {
              try {
                const _res = JSON.parse(currentJson) as {[key: string]: IExpressionConfig}
                setMapValue(_res)
                if (onChange) onChange(_res)
                if (onRunExpression) onRunExpression(_res)
              } catch (error) {
                console.error('invalid json', currentJson, error)
                return
              }
            }}
          >
            <Modal.Content>
              <textarea
                autoFocus
                value={currentJson}
                onChange={(e) => setCurrentJson(e.target.value || '')}
                onFocus={(e) => e.target.select()}
                style={{
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  minHeight: '200px',
                }}
              />
            </Modal.Content>
          </Modal>
        </div>
      : null}
      <div className='items'>
        {_.map(keys || _.keys(values), (k) => (
          <div key={k}>
            <div className='header'>{k}</div>
            <ExpressionEditor
              instanceID={k}
              // showExpression={false}
              value={mapValue[k]}
              onChange={(conf) => {
                setMapValue((prev) => ({
                  ...prev,
                  [k]: conf,
                }))
              }}
              onRunExpression={(data) => handleOnRunExpression(k, data)}
              {...rest}
            />
            {errors && errors[k] && errors[k].length > 0 ?
              <div
                style={{
                  padding: '0.5em',
                }}
              >
                <ErrorMessage message={`${k}: expression errors encountered`}>
                  <JsonViewer
                    expanded
                    canClose={false}
                    collapseStringsAfterLength={false}
                    data={errors[k]}
                  />
                </ErrorMessage>
              </div>
            : null}
          </div>
        ))}
      </div>
    </div>
  )
}
